import { type Subproject as CardSubproject } from '@/organization/landing/components/projectCard/types';
import { getOverallStatus } from '@/organization/landing/components/projectCard/utils';
import { getSubprojectStatusIcon } from '@/organization/landing/statusUtils';
import { type Subproject } from '@/stores/SubprojectStore';
import { alpha, Box, Typography, useTheme } from '@mui/material';

/** Cast SubprojectStore.Subproject to projectCard Subproject for getOverallStatus */
function toCardSubproject(s: Subproject): CardSubproject {
  return s as unknown as CardSubproject;
}

interface SubprojectProgressSummaryProps {
  subprojects: Subproject[];
}

/**
 * Renders summary chip groups when there are more than 10 subprojects.
 * Shows count + status icon for each status category (finished, in-progress, not-started).
 */
export function SubprojectProgressSummary({
  subprojects,
}: SubprojectProgressSummaryProps) {
  const finishedCount = subprojects.filter(
    (s) => getOverallStatus(toCardSubproject(s)) === 'finished',
  ).length;
  const inProgressCount = subprojects.filter(
    (s) => getOverallStatus(toCardSubproject(s)) === 'in-progress',
  ).length;
  const notStartedCount = subprojects.filter(
    (s) => getOverallStatus(toCardSubproject(s)) === 'not-started',
  ).length;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 'clamp(10px, 0.8vw, 16px)',
        alignItems: 'center',
      }}
    >
      <SummaryChipGroup count={finishedCount} status='finished' />
      <SummaryChipGroup count={inProgressCount} status='in-progress' />
      <SummaryChipGroup count={notStartedCount} status='not-started' />
    </Box>
  );
}

function SummaryChipGroup({
  count,
  status,
}: {
  count: number;
  status: 'finished' | 'in-progress' | 'not-started';
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 'clamp(4px, 0.35vw, 6px)',
        alignItems: 'center',
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 500,
          fontSize: 'clamp(9px, 0.625vw, 12px)',
          color: alpha(theme.palette.common.white, 0.7),
        }}
      >
        {count}
      </Typography>
      <Box
        component='img'
        src={getSubprojectStatusIcon(status)}
        alt={status}
        sx={{
          width: 'clamp(80px, 3.5vw, 68px)',
          height: 'clamp(12px, 0.9vw, 18px)',
          display: 'block',
          imageRendering: 'crisp-edges',
        }}
      />
    </Box>
  );
}
